import AsyncStorage from '@react-native-async-storage/async-storage';
let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Image,
  ActivityIndicator,
  Alert,
  Linking,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
export default function AssessmentDetailsScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_assessmentdetails`;
    apiFetch('/api/data/assessments?sort=created_at&order=desc&limit=20').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // States
  const [isLoading, setIsLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('instructions');

  // Mock assessment data
  const mockAssessments = [
    {
      id: '1',
      title: 'Mechanical Engineering Aptitude Test',
      jobPosition: 'Senior Mechanical Engineer',
      company: 'Rolls-Royce',
      logo: 'https://logo.clearbit.com/rolls-royce.com',
      status: 'Not Started',
      dueDate: '10 June 2025',
      duration: '60 minutes',
      instructions: [
        'This assessment consists of 30 multiple-choice questions.',
        'You will have 60 minutes to complete the test.',
        'Ensure you have a stable internet connection.',
        'You cannot pause the test once started.',
        'The test covers topics in thermodynamics, fluid mechanics, and materials science.',
        'A calculator is permitted.',
        'Results will be shared within 48 hours of completion.',
      ],
      topics: [
        { name: 'Thermodynamics', weight: '30%' },
        { name: 'Fluid Mechanics', weight: '30%' },
        { name: 'Materials Science', weight: '25%' },
        { name: 'Engineering Mechanics', weight: '15%' },
      ],
      aiPrepTips: [
        'Review fundamental principles of the Brayton cycle.',
        'Practice problems involving stress-strain relationships.',
        "Understand the basics of fluid dynamics, including Bernoulli's principle.",
        'Focus on material properties like elasticity and plasticity.',
      ],
      result: null,
    },
    {
      id: '2',
      title: 'Project Management Simulation',
      jobPosition: 'Project Manager',
      company: 'BAE Systems',
      logo: 'https://logo.clearbit.com/baesystems.com',
      status: 'Completed',
      dueDate: '5 June 2025',
      duration: '90 minutes',
      instructions: [
        'This is a simulation-based assessment.',
        'You will manage a virtual project through various scenarios.',
        'Decisions made will impact project outcomes (budget, timeline, quality).',
        'There are 5 key decision points in the simulation.',
        'Focus on risk management, stakeholder communication, and resource allocation.',
        'Your performance will be evaluated based on project success metrics.',
      ],
      topics: [
        { name: 'Risk Management', weight: '25%' },
        { name: 'Stakeholder Communication', weight: '25%' },
        { name: 'Resource Allocation', weight: '20%' },
        { name: 'Budget Management', weight: '15%' },
        { name: 'Timeline Management', weight: '15%' },
      ],
      aiPrepTips: [
        'Familiarize yourself with common project management methodologies (Agile, Waterfall).',
        'Practice identifying and mitigating project risks.',
        'Consider different communication strategies for various stakeholders.',
        'Think about trade-offs between scope, time, and cost.',
      ],
      result: {
        score: 88,
        status: 'Passed',
        feedback: [
          'Excellent risk identification and mitigation strategies.',
          'Strong stakeholder communication skills demonstrated.',
          'Effective resource allocation throughout the project lifecycle.',
          'Budget management could be slightly improved in early stages.',
        ],
        completionDate: '4 June 2025',
      },
    },
    {
      id: '3',
      title: 'Python Coding Challenge',
      jobPosition: 'Software Developer',
      company: 'Dyson',
      logo: 'https://logo.clearbit.com/dyson.com',
      status: 'In Progress',
      dueDate: '12 June 2025',
      duration: '120 minutes',
      instructions: [
        'This assessment includes 3 coding problems.',
        'You can use Python 3.8.',
        'Problems cover data structures, algorithms, and object-oriented programming.',
        'You can run your code against test cases.',
        'Code efficiency and correctness will be evaluated.',
        'Plagiarism checks will be performed.',
      ],
      topics: [
        { name: 'Data Structures', weight: '35%' },
        { name: 'Algorithms', weight: '35%' },
        { name: 'Object-Oriented Programming', weight: '20%' },
        { name: 'Problem Solving', weight: '10%' },
      ],
      aiPrepTips: [
        'Practice common algorithm problems (sorting, searching, graph traversal).',
        "Review Python's built-in data structures (lists, dictionaries, sets).",
        'Understand time and space complexity (Big O notation).',
        'Practice writing clean, well-documented code.',
      ],
      result: null,
      progress: {
        completed: 1,
        total: 3,
        timeLeft: '45 minutes',
      },
    },
  ];

  // Load assessment details when component mounts or route params change
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setAssessment(null);

    if (route.params?.assessmentId) {
      loadAssessmentDetails(route.params.assessmentId);
    } else {
      setError('No assessment ID provided');
      setIsLoading(false);
    }
  }, [route.params?.assessmentId]);

  // Load assessment details
  const loadAssessmentDetails = assessmentId => {
    if (!assessmentId) {
      setError('Invalid assessment ID');
      setIsLoading(false);
      return;
    }

    // Simulate API call
    setTimeout(() => {
      try {
        const assessmentData = mockAssessments.find(a => a.id === assessmentId);

        if (!assessmentData) {
          setError(`Assessment with ID ${assessmentId} not found`);
          setIsLoading(false);
          return;
        }

        setAssessment(assessmentData);
        setError(null);
      } catch (err) {
        setError('Error loading assessment details');
        console.error('Error loading assessment details:', err);
      } finally {
        setIsLoading(false);
      }
    }, 1000);
  };

  // Get status color based on assessment status
  const getStatusColor = status => {
    switch (status) {
      case 'Not Started':
        return '#64748B'; // Gray
      case 'In Progress':
        return '#f59e0b'; // Orange
      case 'Completed':
        return '#0e4e96'; // Blue
      case 'Passed':
        return '#10b981'; // Green
      case 'Failed':
        return '#dc2626'; // Red
      default:
        return '#64748B';
    }
  };

  // Start or continue assessment
  const handleStartAssessment = () => {
    if (!assessment) return;

    const action = assessment.status === 'Not Started' ? 'start' : 'continue';
    const title =
      assessment.status === 'Not Started'
        ? 'Start Assessment'
        : 'Continue Assessment';
    const message =
      assessment.status === 'Not Started'
        ? `Are you sure you want to start the ${assessment.title}? You will have ${assessment.duration} to complete it.`
        : `Do you want to continue the ${assessment.title}? You have ${assessment.progress?.timeLeft || 'time'} remaining.`;

    Alert.alert(title, message, [
      {
        text: 'Cancel',
        style: 'cancel',
      },
      {
        text: action.charAt(0).toUpperCase() + action.slice(1),
        onPress: () => {
          // Simulate opening assessment link or starting process
          console.log(`${action} assessment: ${assessment.id}`);
          // In a real app, you might navigate to a webview or another screen
          // Linking.openURL('https://assessment.platform.com/start/' + assessment.id);

          // Update status locally if starting
          if (action === 'start') {
            setAssessment({ ...assessment, status: 'In Progress' });
          }
        },
      },
    ]);
  };

  // Render assessment details
  const renderAssessmentDetails = () => {
    if (!assessment) return null;

    const statusColor = getStatusColor(assessment.status);
    const resultStatusColor = assessment.result
      ? getStatusColor(assessment.result.status)
      : null;

    return (
      <ScrollView
        style={styles.contentContainer}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.assessmentHeader}>
          <Image
            source={{ uri: (assessment.logo && assessment.logo.startsWith('http')) ? assessment.logo : undefined }}
            style={styles.companyLogo}
            resizeMode="contain"
            defaultSource={require('../../../assets/default-company-logo.png')}
          />
          <View style={styles.assessmentHeaderInfo}>
            <Text style={styles.assessmentTitle}>{assessment.title}</Text>
            <Text style={styles.companyName}>{assessment.company}</Text>
            <Text style={styles.jobPosition}>{assessment.jobPosition}</Text>
          </View>
        </View>

        <View style={styles.statusSection}>
          <View
            style={[
              styles.statusBadge,
              { backgroundColor: statusColor + '20' }, // 20% opacity
            ]}
          >
            <Text style={[styles.statusText, { color: statusColor }]}>
              {assessment.status}
            </Text>
          </View>
          {assessment.progress && (
            <Text style={styles.progressText}>
              {assessment.progress.completed}/{assessment.progress.total}{' '}
              sections completed ({assessment.progress.timeLeft} remaining)
            </Text>
          )}
        </View>

        <View style={styles.assessmentQuickInfo}>
          <View style={styles.quickInfoItem}>
            <Ionicons name="calendar-outline" size={16} color="#dc2626" />
            <Text style={styles.quickInfoText}>Due: {assessment.dueDate}</Text>
          </View>
          <View style={styles.quickInfoItem}>
            <Ionicons name="time-outline" size={16} color="#0e4e96" />
            <Text style={styles.quickInfoText}>
              Duration: {assessment.duration}
            </Text>
          </View>
        </View>

        <View style={styles.tabsContainer}>
          <TouchableOpacity
            style={[
              styles.tab,
              activeTab === 'instructions' && styles.activeTab,
            ]}
            onPress={() => setActiveTab('instructions')}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === 'instructions' && styles.activeTabText,
              ]}
            >
              Instructions
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'topics' && styles.activeTab]}
            onPress={() => setActiveTab('topics')}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === 'topics' && styles.activeTabText,
              ]}
            >
              Topics
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'prep' && styles.activeTab]}
            onPress={() => setActiveTab('prep')}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === 'prep' && styles.activeTabText,
              ]}
            >
              AI Prep Tips
            </Text>
          </TouchableOpacity>
          {assessment.result && (
            <TouchableOpacity
              style={[styles.tab, activeTab === 'results' && styles.activeTab]}
              onPress={() => setActiveTab('results')}
            >
              <Text
                style={[
                  styles.tabText,
                  activeTab === 'results' && styles.activeTabText,
                ]}
              >
                Results
              </Text>
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.tabContent}>
          {activeTab === 'instructions' && (
            <View>
              <Text style={styles.sectionTitle}>Instructions</Text>
              {assessment.instructions.map((instruction, index) => (
                <View key={index} style={styles.instructionItem}>
                  <Text style={styles.bulletPoint}>•</Text>
                  <Text style={styles.instructionText}>{instruction}</Text>
                </View>
              ))}
            </View>
          )}

          {activeTab === 'topics' && (
            <View>
              <Text style={styles.sectionTitle}>Topics Covered</Text>
              <View style={styles.topicsContainer}>
                {assessment.topics.map((topic, index) => (
                  <View key={index} style={styles.topicItem}>
                    <Text style={styles.topicName}>{topic.name}</Text>
                    <Text style={styles.topicWeight}>{topic.weight}</Text>
                  </View>
                ))}
              </View>
            </View>
          )}

          {activeTab === 'prep' && (
            <View>
              <Text style={styles.sectionTitle}>AI Preparation Tips</Text>
              {assessment.aiPrepTips.map((tip, index) => (
                <View key={index} style={styles.prepTipItem}>
                  <Ionicons name="bulb-outline" size={16} color="#0e4e96" />
                  <Text style={styles.prepTipText}>{tip}</Text>
                </View>
              ))}
            </View>
          )}

          {activeTab === 'results' && assessment.result && (
            <View>
              <Text style={styles.sectionTitle}>Assessment Results</Text>
              <View style={styles.resultSummary}>
                <View
                  style={[
                    styles.resultStatusBadge,
                    { backgroundColor: resultStatusColor + '20' },
                  ]}
                >
                  <Text
                    style={[
                      styles.resultStatusText,
                      { color: resultStatusColor },
                    ]}
                  >
                    {assessment.result.status}
                  </Text>
                </View>
                <Text style={styles.resultScore}>
                  Score: {assessment.result.score}%
                </Text>
                <Text style={styles.completionDate}>
                  Completed on: {assessment.result.completionDate}
                </Text>
              </View>

              <Text style={styles.feedbackTitle}>Feedback</Text>
              {assessment.result.feedback.map((feedbackItem, index) => (
                <View key={index} style={styles.feedbackItem}>
                  <Ionicons name="checkmark-circle" size={16} color="#10b981" />
                  <Text style={styles.feedbackText}>{feedbackItem}</Text>
                </View>
              ))}
            </View>
          )}
        </View>

        {['Not Started', 'In Progress'].includes(assessment.status) && (
          <TouchableOpacity
            style={styles.actionButton}
            onPress={handleStartAssessment}
          >
            <Text style={styles.actionButtonText}>
              {assessment.status === 'Not Started'
                ? 'Start Assessment'
                : 'Continue Assessment'}
            </Text>
            <Ionicons name="arrow-forward" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        )}

        {assessment.status === 'Completed' && assessment.result && (
          <TouchableOpacity
            style={[styles.actionButton, styles.viewResultsButton]}
            onPress={() => setActiveTab('results')}
          >
            <Text style={styles.viewResultsButtonText}>View Results</Text>
            <Ionicons name="analytics-outline" size={20} color="#0e4e96" />
          </TouchableOpacity>
        )}
      </ScrollView>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>

        <Text style={styles.headerTitle}>Assessment Details</Text>

        <TouchableOpacity
          style={styles.headerButton}
          onPress={() => {
            // Show help or contact support
            Alert.alert(
              'Need Help?',
              'If you have questions about this assessment, please contact the recruiter.',
              [{ text: 'OK' }]
            );
          }}
        >
          <Ionicons name="help-circle-outline" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      {/* Content */}
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading assessment details...</Text>
        </View>
      ) : error ? (
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={60} color="#dc2626" />
          <Text style={styles.errorTitle}>Error</Text>
          <Text style={styles.errorDescription}>{error}</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() => loadAssessmentDetails(route.params?.assessmentId)}
          >
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      ) : (
        renderAssessmentDetails()
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerButton: {
    padding: 8,
  },
  contentContainer: {
    flex: 1,
    padding: 16,
  },
  assessmentHeader: {
    flexDirection: 'row',
    marginBottom: 16,
  },
  companyLogo: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginRight: 16,
    backgroundColor: '#F1F5F9',
  },
  assessmentHeaderInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  assessmentTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  companyName: {
    fontSize: 16,
    color: '#334155',
    marginBottom: 2,
  },
  jobPosition: {
    fontSize: 14,
    color: '#64748b',
  },
  statusSection: {
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600',
  },
  progressText: {
    fontSize: 14,
    color: '#64748b',
    marginLeft: 12,
  },
  assessmentQuickInfo: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 5,
    elevation: 2,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  quickInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  quickInfoText: {
    fontSize: 14,
    color: '#334155',
    marginLeft: 8,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#F1F5F9',
    padding: 4,
    borderRadius: 8,
    marginBottom: 16,
  },
  tab: {
    flex: 1,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
  },
  activeTab: {
    backgroundColor: '#FFFFFF',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748b',
  },
  activeTabText: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  tabContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 5,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 12,
  },
  instructionItem: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  bulletPoint: {
    fontSize: 14,
    color: '#334155',
    marginRight: 8,
    lineHeight: 20,
  },
  instructionText: {
    fontSize: 14,
    color: '#334155',
    lineHeight: 20,
    flex: 1,
  },
  topicsContainer: {
    marginBottom: 16,
  },
  topicItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  topicName: {
    fontSize: 14,
    color: '#334155',
  },
  topicWeight: {
    fontSize: 14,
    color: '#64748b',
    fontWeight: '500',
  },
  prepTipItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  prepTipText: {
    fontSize: 14,
    color: '#334155',
    marginLeft: 8,
    flex: 1,
    lineHeight: 20,
  },
  resultSummary: {
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  resultStatusBadge: {
    alignSelf: 'flex-start',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginBottom: 8,
  },
  resultStatusText: {
    fontSize: 14,
    fontWeight: '600',
  },
  resultScore: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  completionDate: {
    fontSize: 14,
    color: '#64748b',
  },
  feedbackTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 12,
  },
  feedbackItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  feedbackText: {
    fontSize: 14,
    color: '#334155',
    marginLeft: 8,
    flex: 1,
    lineHeight: 20,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0e4e96',
    paddingVertical: 14,
    borderRadius: 8,
    marginTop: 8,
    marginBottom: 24,
  },
  actionButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginRight: 8,
  },
  viewResultsButton: {
    backgroundColor: '#F1F5F9',
  },
  viewResultsButtonText: {
    color: '#0e4e96',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 12,
    fontSize: 16,
    color: '#64748b',
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#dc2626',
    marginTop: 12,
    marginBottom: 8,
  },
  errorDescription: {
    fontSize: 16,
    color: '#64748b',
    textAlign: 'center',
    marginBottom: 20,
  },
  retryButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
